import { TZDate } from '@date-fns/tz';
import { noddiAsync, URLKeys } from 'noddi-async';
import type { NoddiAsyncError } from 'noddi-types';
import { useCallback, useMemo } from 'react';
import {
  groupSlotsIntoDays,
  mergeCapacitySlotsWithContributions,
  normalizeAvailableSlots,
  withHeldWindow,
  type CapacitySlotLabelMode,
  type HeldSlotWindow,
  type NormalizedCapacitySlot
} from './capacityUtils';
import type { DayWithSlots } from './types';

/**
 * Poll while a sell picker is on screen. Free starts go stale the moment another
 * customer reserves, and a proposal page is often read for minutes before a tile is
 * tapped. Paused while the tab is in the background (React Query default).
 */
const SELL_SLOT_REFRESH_INTERVAL_MS = 60_000;

export type UseStationaryCapacityQueryInput = {
  garageId?: number;
  dateFrom: string;
  dateTo: string;
  salesItemIds?: number[];
  serviceSeconds?: number;
  applyBookingLeadTime?: boolean;
  /** Pin slots to one bay. Omit to auto-assign (all bays, first free one wins). */
  workstationId?: number | null;
  /** Named contribution rows for hover tooling; failures leave public counts. */
  detailed?: boolean;
  /** @default garageId != null */
  enabled?: boolean;
  /** Visible range dates for stable day grouping. */
  rangeDates?: Date[];
  labelMode?: CapacitySlotLabelMode;
  /**
   * Sell surfaces: refetch on mount/focus/reconnect and poll, so a tile the customer
   * can tap was free seconds ago rather than whenever the page happened to load.
   * Ops overviews leave it off — they read capacity, they do not sell it.
   */
  keepFresh?: boolean;
  /**
   * The window this customer is holding. Their own hold consumes the bay, so a
   * refetch drops it from free starts; re-adding it keeps the selection on screen.
   */
  heldWindow?: HeldSlotWindow | null;
};

export type UseStationaryCapacityQueryReturn = {
  days: DayWithSlots[];
  slots: NormalizedCapacitySlot[];
  slotLookup: Map<string, NormalizedCapacitySlot>;
  totalWorkstations: number | undefined;
  /** Garage IANA timezone when known (for navigation “today” in consumers). */
  timeZone: string | undefined;
  isPending: boolean;
  error: NoddiAsyncError | null;
};

/** Shared stationary capacity fetch + collapse/group. Owns garage timezone for labels/grouping. */
export function useStationaryCapacityQuery({
  garageId,
  dateFrom,
  dateTo,
  salesItemIds,
  serviceSeconds,
  applyBookingLeadTime,
  workstationId,
  detailed = false,
  enabled,
  rangeDates,
  labelMode = 'start-only',
  keepFresh = false,
  heldWindow
}: UseStationaryCapacityQueryInput): UseStationaryCapacityQueryReturn {
  const isEnabled = enabled ?? garageId != null;

  const {
    data: garage,
    isPending: isGaragePending,
    error: garageError
  } = noddiAsync.useGet({
    type: URLKeys.getGarage,
    input: { garageId: garageId! },
    queryConfig: { enabled: garageId != null }
  });

  const timeZone = garage?.address.timezone.value;

  const toZoned = useCallback(
    (value: string): Date => (timeZone ? new TZDate(new Date(value), timeZone) : new Date(value)),
    [timeZone]
  );

  const slotsInput = {
    garageId: garageId!,
    dateFrom,
    dateTo,
    salesItemIds,
    serviceSeconds,
    applyBookingLeadTime,
    detailed
  };

  // Free starts are volatile: another customer reserving invalidates them, and the
  // backend only caches them for seconds, so a refetch really does return live data.
  const freshnessConfig = keepFresh
    ? {
        staleTime: 0,
        refetchOnMount: 'always' as const,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchInterval: SELL_SLOT_REFRESH_INTERVAL_MS
      }
    : {};

  const {
    data: capacitySlots,
    isPending: isSlotsPending,
    error: slotsError
  } = noddiAsync.useGet({
    type: URLKeys.getStationaryCapacitySlots,
    input: slotsInput,
    queryConfig: { ...freshnessConfig, enabled: isEnabled }
  });

  // Named contribution rows are optional supply detail — failures leave public counts.
  const { data: capacitySlotContributions, error: _capacitySlotContributionsError } = noddiAsync.useGet({
    type: URLKeys.getStationaryCapacitySlotContributions,
    input: slotsInput,
    queryConfig: {
      ...freshnessConfig,
      enabled: detailed && isEnabled,
      retry: false
    }
  });
  void _capacitySlotContributionsError;

  const mergedSlots = useMemo(
    () => mergeCapacitySlotsWithContributions(capacitySlots?.slots ?? [], capacitySlotContributions?.slots, detailed),
    [capacitySlots?.slots, capacitySlotContributions?.slots, detailed]
  );

  // Dropping the other bays makes the wall-clock collapse pin the requested one,
  // so the hold and the created booking both land on the bay the user picked.
  const baySlots = useMemo(
    () => (workstationId == null ? mergedSlots : mergedSlots.filter((slot) => slot.workstationId === workstationId)),
    [mergedSlots, workstationId]
  );

  // Primitives, not the caller's object: a fresh `heldWindow` literal each render
  // would otherwise re-collapse and re-group every tile on every render.
  const heldStart = heldWindow?.start;
  const heldEnd = heldWindow?.end;
  const heldWorkstationId = heldWindow?.workstationId ?? null;

  const availableSlots = useMemo(
    () =>
      withHeldWindow(
        baySlots,
        heldStart != null && heldEnd != null
          ? { start: heldStart, end: heldEnd, workstationId: heldWorkstationId }
          : null
      ),
    [baySlots, heldStart, heldEnd, heldWorkstationId]
  );

  // Pinned to one bay there is no "2 of 5" to show, so suppress the ratio.
  const totalWorkstations = workstationId != null ? 1 : capacitySlots?.workstations.length;

  const slots = useMemo(
    () => normalizeAvailableSlots(availableSlots, toZoned, labelMode),
    [availableSlots, toZoned, labelMode]
  );

  const { days, slotLookup } = useMemo(
    () => groupSlotsIntoDays(slots, rangeDates, toZoned, totalWorkstations),
    [slots, rangeDates, toZoned, totalWorkstations]
  );

  return {
    days,
    slots,
    slotLookup,
    totalWorkstations,
    timeZone,
    // Wait for garage TZ before showing tiles so day buckets do not jump after load.
    isPending: !isEnabled || isSlotsPending || (garageId != null && isGaragePending),
    error: slotsError ?? garageError ?? null
  };
}
